/* ============
 * Bootstrap File
 * ============
 *
 * Will configure and bootstrap the application.
 */

import Cookie from 'tiny-cookie';
import { ElNotification } from 'element-plus';
import router from '@/plugins/router';

/*
 * A deploy removes the previously built, hash-named chunks. A tab opened before that deploy
 * still asks for the old chunk and the host answers the SPA fallback (index.html, HTTP 200),
 * so the browser rejects the module and vue-router cancels the navigation silently.
 * Reload the target URL so the user lands on the freshly deployed chunk instead of being
 * stuck on the current screen. The session flag is cleared on the next successful
 * navigation, so a chunk that is genuinely broken is reloaded once and never loops.
 */
const CHUNK_RELOAD_FLAG = 'chunkReloadAttempt';

// The MIME / "Failed to load module script" wordings are what a SPA fallback answering
// 200 + index.html for a removed chunk produces (SP-14603, SP-14620, SP-14621).
const isChunkLoadError = (error) => /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|is not a valid JavaScript MIME type|Failed to load module script|Unable to preload CSS/i.test(String(error && error.message));

/*
 * No Pinia store of this app is persisted, and the checkout funnel carries its whole state
 * in memory: reloading one of its steps empties the store, bounces the user back to the
 * first step and invites them to order again — the very duplicate we are fixing. Those
 * routes handle the failure themselves instead.
 */
const isMemoryOnlyState = (fullPath) => fullPath.startsWith('/checkout/');

/*
 * Reload `fullPath` once to pick up the current build. Returns true when the reload is
 * under way, false when it is refused (checkout funnel, already tried for this path, or no
 * sessionStorage to guard against a loop).
 */
export function reloadForStaleChunk(fullPath) {
  if (isMemoryOnlyState(fullPath)) {
    return false;
  }

  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_FLAG) === fullPath) {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, fullPath);
  } catch (storageError) {
    // Without the flag we cannot tell a first attempt from a loop, so we do not reload.
    window.console.error(storageError);
    return false;
  }

  window.location.assign(fullPath);
  return true;
}

// Lets the `vite:preloadError` listener of main.js leave a failing navigation to
// `router.onError`, the only one that knows where the user was heading. It holds the
// target of the latest navigation rather than a boolean: a superseded navigation (two 401
// each pushing /login, SP-15244) settles while the latest one is still loading its chunk,
// and must not clear the latest one's tracking.
let pendingNavigation = null;

export const isNavigationPending = () => pendingNavigation !== null;

const settleNavigation = (to) => {
  if (to === pendingNavigation) {
    pendingNavigation = null;
  }
};

router.beforeEach((to) => {
  pendingNavigation = to;
});

/*
 * `triggerError()` of vue-router rejects the navigation promise even once an `onError`
 * handler has dealt with the error. `<router-link>` hides that rejection (its `navigate()`
 * ends with `.catch(noop)`), but the programmatic `$router.push()/replace()` call sites do
 * not, so a stale chunk we already reload for still surfaced as an uncaught
 * `TypeError: Failed to fetch dynamically imported module` (SP-14740, reported from the
 * account settings chunk reached through `SettingWheel`).
 *
 * The errors below are the ones a reload is really under way for, and nothing else. They
 * are keyed by instance — `triggerError()` hands the handler the very error it then
 * rejects with — rather than by a shared flag or the target path: two navigations can be
 * failing at once, and their `onError` run synchronously while the `catch` below runs a
 * microtask later, so any shared state would let one navigation answer for the other.
 */
const reloadingChunkErrors = new WeakSet();

router.onError((error, to) => {
  settleNavigation(to);

  if (!isChunkLoadError(error)) {
    // Registering a handler replaces the router's own reporting: keep the diagnostic.
    window.console.error(error);
    return;
  }

  if (reloadForStaleChunk(to.fullPath) && error instanceof Object) {
    reloadingChunkErrors.add(error);
  }
});

/*
 * Settle the rejection of a navigation whose stale chunk is already being reloaded, and
 * only that one: a chunk error we deliberately did not reload for (checkout funnel, loop
 * guard) and every other navigation error must keep reaching the error reporting.
 */
const settleHandledChunkError = (navigation) => navigation.catch((error) => {
  if (error instanceof Object && reloadingChunkErrors.has(error)) {
    return undefined;
  }

  throw error;
});

const pushRoute = router.push.bind(router);
const replaceRoute = router.replace.bind(router);

router.push = (...args) => settleHandledChunkError(pushRoute(...args));
router.replace = (...args) => settleHandledChunkError(replaceRoute(...args));

router.afterEach((to, from, failure) => {
  settleNavigation(to);

  if (failure) {
    return;
  }

  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
  } catch (storageError) {
    window.console.error(storageError);
  }
});

/* ============
 * Axios
 * ============
 *
 * https://github.com/axios/axios
 */
import AxiosCancel from 'axios-cancel';
import http from '@/plugins/http';
import authService from '@/services/auth';

http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error && error.response && error.response.status === 401) {
      authService.logout();
    }
    return Promise.reject(error);
  },
);

http.interceptors.request.use(
  (config) => {
    http.defaults.headers.common['Cache-Control'] = 'no-cache';
    const origin = Cookie.get('origin');
    if (origin) {
      http.defaults.headers.common['X-Origin'] = origin;
    }
    const campaign = Cookie.get('utm_campaign');
    if (campaign) {
      http.defaults.headers.common['X-Utm-Campaign'] = campaign;
    }
    const medium = Cookie.get('utm_medium');
    if (medium) {
      http.defaults.headers.common['X-Utm-Medium'] = medium;
    }
    const source = Cookie.get('utm_source');
    if (source) {
      http.defaults.headers.common['X-Utm-Source'] = source;
    }

    // SP-14892 — read on every call: on a reload, /me leaves before the first router guard runs.
    const activeCompany = localStorage.getItem('instead-of-customer');
    if (activeCompany) {
      config.headers['X-Instead-Of-Customer'] = activeCompany;
    }

    return config;
  },
  (error) => {
    if (error && error.response && error.response.status === 401) {
      authService.logout();
    }
    return Promise.reject(error);
  },
);

AxiosCancel(http, {
  debug: process.env.NODE_ENV !== 'production',
});

/* ============
 * Pinia
 * ============
 *
 * The store of the application. L'instance Pinia est branchée sur
 * l'application dans `main.js` (`app.use(pinia)`).
 *
 * https://pinia.vuejs.org/
 */
import { useAccountStore } from '@/stores/account';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';

useAuthStore().check();

/* ============
 * Vue Router
 * ============
 *
 * The official Router for Vue.js. It deeply integrates with Vue.js core
 * to make building Single Page Applications with Vue.js a breeze.
 *
 * http://router.vuejs.org/en/index.html
 */
router.beforeEach(async (to) => {
  const { code, state } = to.query;

  // if request comes from the client app, logout the user and redirect to login if no token was given (from app, login is done with a token)
  if (to.query.from_app === 'true') {
    sessionStorage.setItem('from_app', 'true');
    if (!to.query.token) {
      await useAccountStore().clear();
      await useAuthStore().logout();

      return { name: 'login.index' };
    }

    return true;
  }

  // Opt-in permission guard (SP-13434) — only routes that explicitly set
  // `meta.requirePerm: true` are protected here, so enabling this does not
  // change access to the many existing routes that carry a `meta.perm` without
  // opting in. An unauthorized direct URL access is redirected to the missions.
  if (to.matched.some((m) => m.meta.requirePerm && m.meta.perm)) {
    const permRoute = to.matched.find((m) => m.meta.requirePerm && m.meta.perm);
    let perms = useAccountStore().permissions;
    if (!perms) {
      perms = JSON.parse(localStorage.getItem('perms') || 'null');
    }

    if (!perms || !perms[permRoute.meta.perm]) {
      return { name: 'job.index' };
    }
  }

  // Opt-in temp-agency guard (SP-13795) — routes flagged `meta.blockTempAgency`
  // are unreachable for customers whose company runs in temp agency mode
  // (invoicing is not handled in the customer area for them). The store may not
  // be hydrated yet on a direct URL access, hence the localStorage fallback
  // written by the account `STORE` mutation.
  if (to.matched.some((m) => m.meta.blockTempAgency)) {
    let { isTempAgencyMode } = useAccountStore();
    if (!useAccountStore().company) {
      // Store not hydrated yet (direct URL access): fall back on the cached flag.
      // Parsing is guarded — a corrupted value must not throw inside `beforeEach`,
      // which would freeze every navigation of the SPA.
      try {
        isTempAgencyMode = JSON.parse(localStorage.getItem('temp-agency-mode') || 'false') === true;
      } catch (e) {
        isTempAgencyMode = false;
      }
    }

    if (isTempAgencyMode) {
      return { name: 'job.index' };
    }
  }

  if (code && state && to.name !== 'auth.auth0') {
    return { name: 'auth.auth0', query: { code, state } };
  }

  if (to.name === 'checkout.index') {
    if (useCompanyStore().activatedNewForm) {
      return { name: 'co.v2.template' };
    }

    return true;
  }

  if (to.matched.some((m) => m.meta.trustedEmail) && useAuthStore().authenticated) {
    return useAuthStore().trustedEmail ? true : { name: 'home.index' };
  }

  if (to.matched.some((m) => m.meta.auth) && !useAuthStore().authenticated && !(code && state)) {
    /*
     * If the user is not authenticated and visits
     * a page that requires authentication, redirect to the login page
     */
    return {
      name: 'login.index',
      query: { redirect: to.path },
    };
  }

  if (to.matched.some((m) => m.meta.guest) && useAuthStore().authenticated && !(code && state)) {
    /*
     * If the user is authenticated and visits
     * an guest page, redirect to the dashboard page
     */
    return { name: 'home.index' };
  }

  return true;
});

router.afterEach((to) => {
  if (window.ineum) {
    window.ineum('page', to.name);
  }

  const { userAgent } = useAuthStore();
  if (userAgent && (userAgent.androidWebview || userAgent.iosWebview)) {
    const { body } = document;
    body.classList.add('sp-in-app');
  }
});

/* ============
 * Vue i18n
 * ============
 *
 * Internationalization plugin of Vue.js.
 *
 * https://kazupon.github.io/vue-i18n/
 */
import { createI18n } from 'vue-i18n';
import messages from './locale';

/*
 * Mode composition : `$t`, `$te` et `$i18n.locale` sont des propriétés globales,
 * le mode legacy les supprimait de l'instance au démontage (CUSTOMER-5G0).
 * Hors composant, le traducteur est `i18n.global`.
 */
export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: 'fr',
  fallbackLocale: 'en',
  // `fr.json` n'a que des clés plates en dot-notation (`a.b.c`), dont 145 sont
  // aussi le préfixe d'une autre clé (`add_sepa.tooltip.text` et
  // `add_sepa.tooltip.text.temp_agency`). La v6 lisait `messages[clé]` ;
  // la v9 découpe la clé en chemin et `$te()` renvoie false pour les 1090
  // clés — `flatJson: true` ne convient pas non plus, l'arbre écraserait
  // une clé sur deux des 145 collisions. On rétablit la lecture à plat.
  messageResolver: (localeMessages, key) => (
    Object.prototype.hasOwnProperty.call(localeMessages, key) ? localeMessages[key] : null
  ),
  // Une trentaine de messages portent du balisage (`<br/>`, liens vers les CGV)
  // et sont rendus via `v-html` : l'avertissement XSS de vue-i18n ne s'applique pas.
  warnHtmlMessage: false,
  // `en.json` est vide à dessein : chaque clé déclencherait deux warnings de
  // repli vers `en`. Les clés absentes de `fr` restent signalées.
  fallbackWarn: false,
  messages,
});

/*
 * vue-i18n 6 renvoyait une chaîne vide quand la clé était vide ; la v9 lève
 * « Invalid argument ». Plusieurs templates traduisent une clé qui n'arrive
 * qu'avec les données (`Helper.vue` et sa config de départ vide), et
 * l'exception interrompt le rendu de toute la page. `app.use(i18n)` copie
 * `i18n.global.t` dans `$t` : la corriger ici, avant, couvre tous les appels.
 */
const { t: translate } = i18n.global;
i18n.global.t = (key, ...args) => (key ? translate(key, ...args) : '');

http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error && error.response && error.response.status === 403) {
      ElNotification.error({
        title: i18n.global.t('toast_message.role_permission_aunthorized.title'),
        message: i18n.global.t('toast_message.role_permission_aunthorized.text'),
      });
      router.push({ path: '/job' });
    }
    return Promise.reject(error);
  },
);

/**
 * Moment configuration
 */
import 'moment';
import 'moment/locale/fr';
